import { DropdownType, SelectDropDown } from '@ui-kit'
import {
  AuthUserClient,
  DropDownOptionType,
  getQueueEntity,
  hideLoader,
  showLoader,
  updateAuthUserSelectedTenant,
} from '@app-store'
import { useEffect, useState } from 'react'
import { RootState, useAppDispatch, useAppSelector } from '../utils/agent.store'
import { DropDownOption } from '../types/types'
import { ClientDropdownProps } from '../types/common'
import { useNavigate } from 'react-router-dom'
import { QueuesType } from '../types/queueConfig.types'
import { resetServiceWorkerCacheOnClientSwitch } from '../utils/service-worker.utils'

const ClientDropdown: React.FC<ClientDropdownProps> = ({
  onClientChange,
  dataQaId = '',
  isGlobalVisible = true,
  onOptionChange,
  setModal,
  isNested = false,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selectedTenantId = useAppSelector(
    (state: RootState) => state.authUser.selectedClientTenant?.id
  )
  const allTenants = useAppSelector(
    (state: RootState) => state?.authUser?.user?.clients
  )
  const [allTenantsOptions, setAllTenantsOptions] = useState<
    DropdownType<DropDownOption>[]
  >([])

  const handleClientChange = async (
    value: DropdownType<DropDownOptionType>,
    name: string,
    nestedOption?: DropdownType<DropDownOptionType>
  ) => {
    if (nestedOption && name) {
      if (onOptionChange) {
        onOptionChange(value)
      } else {
        dispatch(updateAuthUserSelectedTenant(parseInt(value.value)))
      }
      navigate(
        `/manage-queue/dynamic-queue/${nestedOption.entity_id}/${nestedOption.layout_id}/${nestedOption.value}`
      )
    } else if (!isNested && name) {
      if (onOptionChange) {
        onOptionChange(value)
      } else {
        dispatch(updateAuthUserSelectedTenant(parseInt(value.value)))
      }
    }
    // await caches.delete('ep-api-cache-v1')
    await resetServiceWorkerCacheOnClientSwitch(value.value, { prewarm: false })
  }

  const getSelectedTenantValue = (selectedTenantId: number) => {
    const selectedTenants = allTenantsOptions.filter(
      (tenant) => tenant.value === selectedTenantId?.toString()
    )
    if (selectedTenants.length > 0) {
      return selectedTenants[0]?.value
    } else if (!isGlobalVisible) {
      dispatch(updateAuthUserSelectedTenant(allTenantsOptions[0]?.value))
      return allTenantsOptions[0]?.value
    } else {
      return '-1'
    }
  }

  const getAllTenantOptions = () => {
    const options: { label: string; value: string }[] = []

    const enabledClients = allTenants?.filter(
      (item: AuthUserClient) => item.enabled
    )
    const globalEntry = enabledClients?.find(
      (item) => item.name_of_organization === 'Global'
    )
    const sortedClients = enabledClients
      ?.filter((item) => item.name_of_organization !== 'Global')
      .sort((a, b) =>
        a.name_of_organization.localeCompare(b.name_of_organization)
      )
    if (isGlobalVisible) {
      if (globalEntry)
        options.push({
          label: globalEntry.name_of_organization,
          value: globalEntry.id?.toString() || '',
        })
      sortedClients?.forEach((item: AuthUserClient) => {
        options.push({
          label: item.name_of_organization,
          value: item.id?.toString() || '',
        })
      })
    } else {
      sortedClients?.forEach((item: AuthUserClient) => {
        options.push({
          label: item.name_of_organization,
          value: item.id?.toString() || '',
        })
      })
    }
    setAllTenantsOptions(options)
  }

  const handleNestedOptions = async (
    e?: DropdownType<DropDownOptionType, string> | null,
    page = 0,
    limit?: number
  ) => {
    if (isNested) {
      try {
        page < 1 && dispatch(showLoader())
        const response = await getQueueEntity(
          e?.value && Number(e?.value) > 0 ? Number(e?.value) : null,
          page ?? 0,
          limit ?? 10
        )

        const content = Array.isArray(response?.data?.content)
          ? response?.data?.content
          : []

        const nestedOptions = content.map((item: QueuesType) => ({
          value: item.id,
          entity_id: item.entity.id,
          layout_id: item.layout.id,
          label: item.name,
        }))

        return nestedOptions
      } catch (error) {
        console.error('Error fetching queue entity:', error)
        return null
      } finally {
        page < 1 && dispatch(hideLoader())
      }
    }
  }

  useEffect(() => {
    getAllTenantOptions()
  }, [allTenants, isGlobalVisible])

  useEffect(() => {
    onClientChange && onClientChange(selectedTenantId)
  }, [selectedTenantId])

  return (
    allTenantsOptions.length && (
      <SelectDropDown
        label=""
        options={allTenantsOptions}
        name="Client"
        onChange={handleClientChange}
        className="bg-secondary-50 h-10 w-[8rem] text-xs"
        defaultValue={getSelectedTenantValue(selectedTenantId ?? -1)}
        isBordered={false}
        dataQaId={dataQaId}
        isSearchable={true}
        isCrossable={false}
        confirmationModal={
          setModal && !isNested
            ? () => {
                return new Promise<boolean>((resolve) => {
                  setModal({
                    state: true,
                    type: 'redirect',
                    resolve,
                  })
                })
              }
            : undefined
        }
        handleNestedOptions={isNested ? handleNestedOptions : undefined}
        checkPosition={false}
      />
    )
  )
}
export default ClientDropdown
